import { ReactNode, useMemo, useState } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  GlobalStyles,
  Stack,
  Theme,
  ThemeProvider,
  Typography,
  useTheme,
} from "@mui/material";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { EditorState } from "lexical";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { HeadingNode } from "@lexical/rich-text";
import { ListNode, ListItemNode } from "@lexical/list";
import { editorStyles, editorTheme, StyledContentEditable } from "./styles";
import { EditorToolbar } from "./EditorToolbar";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { useTranslation } from "react-i18next";
import { BrandColors, Branding } from "types";
import { PreviewMode } from "./EditorToolbar";
import {
  AUTURA_ASPHALT,
  AUTURA_ASPHALT_LIGHT,
  getBrandedTheme,
} from "context/themeContext";
import { logError } from "utils";

function onError(error: Error) {
  logError(error);
}

interface RichTextEditorProps {
  serializedEditorState: string | undefined;
  setSerializedEditorState: (editorState: string) => void;
  branding?: Branding;
  canEdit: boolean;
}

export default function TextEditor({
  serializedEditorState,
  setSerializedEditorState,
  branding,
  canEdit,
}: RichTextEditorProps): ReactNode {
  const tenantTheme = useTheme();
  const { t } = useTranslation();
  const [previewMode, setPreviewMode] = useState<PreviewMode>("md");

  const previewTheme: Theme = useMemo(() => {
    const _propertyColors: BrandColors = {
      primary: branding?.primary || tenantTheme.palette.primary.main,
      secondary: AUTURA_ASPHALT,
    };
    return getBrandedTheme(_propertyColors);
  }, [branding?.primary]);

  const handleEditorStateChange = (editorState: EditorState) => {
    setSerializedEditorState(JSON.stringify(editorState.toJSON()));
  };

  return (
    <>
      <GlobalStyles styles={editorStyles} />
      <LexicalComposer
        initialConfig={{
          namespace: "TextEditor",
          theme: editorTheme,
          onError,
          nodes: [HeadingNode, ListNode, ListItemNode],
          editorState: serializedEditorState,
          editable: canEdit,
        }}
      >
        <OnChangePlugin
          onChange={handleEditorStateChange}
          ignoreSelectionChange
        />
        <ListPlugin />
        <HistoryPlugin />
        <TabIndentationPlugin />

        <EditorToolbar
          previewMode={previewMode}
          setPreviewMode={setPreviewMode}
        />

        <ThemeProvider theme={previewTheme}>
          <Container
            maxWidth={previewMode}
            sx={{ position: "relative", pb: 2 }}
          >
            <Card
              raised
              elevation={3}
              component={"main"}
              sx={{
                minHeight: "50vh",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                backgroundColor: "#ffffff",
                borderRadius: "0px",
                border: `1px solid ${AUTURA_ASPHALT_LIGHT}`,
                px: 1,
                py: 4,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  backgroundColor: "white",
                  textTransform: "capitalize",
                  p: 3,
                }}
              >
                {t("permit_portal.t_and_c", { ns: "permits" })}
              </Typography>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ overflowY: "auto" }}>
                  <RichTextPlugin
                    contentEditable={<StyledContentEditable />}
                    ErrorBoundary={LexicalErrorBoundary}
                  />
                </Box>
                <Stack
                  direction={"row"}
                  justifyContent={"flex-end"}
                  width={"100%"}
                  display={"flex"}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    pt: 4,
                    pb: 2,
                  }}
                >
                  <Button
                    variant={"contained"}
                    color="primary"
                    sx={{ height: 40, borderRadius: "100px", width: 150 }}
                  >
                    {t("permit_portal.agree", { ns: "permits" })}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Container>
        </ThemeProvider>
      </LexicalComposer>
    </>
  );
}
